import axios from "axios";
import * as React from "react";
import { Table } from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FormikProps } from "formik";
import * as yup from "yup";
import "./App.css"; // Import the new CSS file
import logo from '../../assets/mideye_logotype@3x.png'; // Import the new logo
import { FaUser, FaLock, FaSignInAlt, FaSignOutAlt, FaKey, FaCheckCircle, FaTimesCircle, FaServer } from 'react-icons/fa';
import { Icon } from '../Icon/Icon';

export interface IAttributeNameValuePair {
  name: string;
  value: string;
}

// Update the IAppState interface to include ipAddress
export interface IAppState {
  attributeNameValuePairs: IAttributeNameValuePair[];
  authenticationPhase: string;
  reference: string;
  replyMessage: string;
  username: string;
  password: string;
  ipAddress?: string;
  selectedProfile?: string; // Add this for profile selection
}

// Update initial values
const initialValues: IAppState = {
  attributeNameValuePairs: [] as IAttributeNameValuePair[],
  authenticationPhase: "first",
  reference: "",
  replyMessage: "",
  username: "",
  password: "",
  ipAddress: "",
  selectedProfile: "default", // Default profile
};

// Validation schema using yup
const validationSchema = yup.object({
  username: yup.string().required("Username is required"),
  password: yup.string().required("Password is required"),
  selectedProfile: yup.string().required("Profile is required"),
});

const challengeValidationSchema = yup.object().shape({
  otp: yup.string().required("OTP is required"),
});

const App = () => {
  const [state, setState] = React.useState(initialValues);
  const [availableProfiles, setAvailableProfiles] = React.useState<string[]>([]);
  const [currentProfile, setCurrentProfile] = React.useState<string>("default");

  React.useEffect(() => {
    // Set document title
    document.title = process.env.REACT_APP_TITLE || 'RADIUS Client';
    
    const getClientIP = async () => {
      try {
        // Using ipify API to get the client's public IP
        const response = await axios.get('https://api.ipify.org?format=json');
        setState(prev => ({
          ...prev,
          ipAddress: response.data.ip
        }));
      } catch (error) {
        console.error('Error fetching IP address:', error);
      }
    };

    const fetchProfiles = async () => {
      try {
        // Get available profiles
        const profilesResponse = await axios.get('/api/profiles');
        const profiles = profilesResponse.data;
        
        if (!profiles || profiles.length === 0) {
          console.error('No RADIUS profiles available');
          setState(prev => ({
            ...prev,
            authenticationPhase: "Reject",
            replyMessage: "No RADIUS profiles are configured. Please contact your administrator."
          }));
          return;
        }
        
        setAvailableProfiles(profiles);
        
        try {
          // Get current profile
          const currentProfileResponse = await axios.get('/api/profiles/current');
          if (currentProfileResponse.data && currentProfileResponse.data.profile) {
            const profile = currentProfileResponse.data.profile;
            console.log(`Setting current profile to: ${profile}`);
            setCurrentProfile(profile);
            
            // Update state with the profile
            setState(prev => ({
              ...prev,
              selectedProfile: profile
            }));
          } else {
            // If no current profile is set, use the first available profile
            const firstProfile = profiles[0];
            console.log(`No current profile set. Using first available profile: ${firstProfile}`);
            setCurrentProfile(firstProfile);
            setState(prev => ({
              ...prev,
              selectedProfile: firstProfile
            }));
          }
        } catch (error) {
          console.error('Error fetching current profile:', error);
          // Use the first available profile as fallback
          const firstProfile = profiles[0];
          console.log(`Error fetching current profile. Using first available profile: ${firstProfile}`);
          setCurrentProfile(firstProfile);
          setState(prev => ({
            ...prev, 
            selectedProfile: firstProfile
          }));
        }
      } catch (error) {
        console.error('Error fetching profiles:', error);
        setState(prev => ({
          ...prev,
          authenticationPhase: "Reject",
          replyMessage: "Unable to load RADIUS profiles. Please contact your administrator."
        }));
      }
    };

    getClientIP();
    fetchProfiles();
  }, []);

  // Update the handleValidSubmit function
  const handleValidSubmit = async (values: IAppState) => {
    try {
      // Make sure we're using the correct profile from the form values
      const selectedProfile = values.selectedProfile || currentProfile;
      console.log(`Authenticating with profile: ${selectedProfile}`);
      
      const request = { 
        ...values, 
        reference: state.reference,
        ipAddress: state.ipAddress,
        // Make sure to use the selectedProfile value directly here
        profile: selectedProfile
      };
      
      const result = await axios.post("api/authenticate", request);
  
      setState({
        ...state,
        username: values.username,
        selectedProfile: selectedProfile, // Store the selected profile
        attributeNameValuePairs: state.attributeNameValuePairs.concat(
            result.data.attributeNameValuePairs
        ),
        authenticationPhase: result.data.authenticationCode,
        reference: result.data.reference,
        replyMessage: result.data.replyMessage,
      });
    } catch (error) {
      console.error('Error during authentication:', error);
      setState({
        ...state,
        authenticationPhase: "Reject",
        replyMessage: "Error connecting to authentication service"
      });
    }
  };

  const handleLogout = async () => {
    await axios.get("api/logout");
    setState({...initialValues, ipAddress: state.ipAddress, selectedProfile: currentProfile});
  };

  const renderFirstPhase = () => {
    return (
      <div className="login-card">
        <div className="logo-container">
          <img src={logo} alt="MidEye Logo" className="logo" />
        </div>
        <div className="login-header">
          <h1 className="login-title">{process.env.REACT_APP_HEADER_TEXT || 'VPN Login'}</h1>
        </div>
        <Formik
          initialValues={{...state, selectedProfile: currentProfile}}
          validationSchema={validationSchema}
          onSubmit={handleValidSubmit}
          enableReinitialize
        >
          {(props) => (
            <Form id="radius-client-form">
              <div className="form-spacing-div">
                {/* Add Profile Selection */}
                <div className="form-group">
                  <label className="form-label" htmlFor="selectedProfile">
                    <Icon icon={FaServer} className="me-2" />RADIUS Profile
                  </label>
                  <Field
                    id="profile-select"
                    name="selectedProfile"
                    as="select"
                    className="form-control"
                    autoFocus
                    tabIndex={1}
                  >
                    {availableProfiles.map(profile => (
                      <option key={profile} value={profile}>{profile}</option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="selectedProfile"
                    component="div"
                    className="field-error"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="username">
                    <Icon icon={FaUser} className="me-2" />Username
                  </label>
                  <Field
                    id="username-input"
                    name="username"
                    type="text"
                    className="form-control"
                    placeholder="Enter your username"
                    tabIndex={2}
                  />
                  <ErrorMessage
                    name="username"
                    component="div"
                    className="field-error"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="password">
                    <Icon icon={FaLock} className="me-2" />Password
                  </label>
                  <Field
                    id="password-input"
                    name="password"
                    type="password"
                    className="form-control"
                    placeholder="Enter your password"
                    tabIndex={3}
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="field-error"
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary" tabIndex={4}>
                <Icon icon={FaSignInAlt} className="me-2" />Sign In
              </button>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  const renderAccept = () => {
    const { attributeNameValuePairs } = state;

    return (
      <div className="login-card">
        <div className="logo-container">
          <img src={logo} alt="MidEye Logo" className="logo" />
        </div>
        <div className="status-message status-success">
          <Icon icon={FaCheckCircle} className="me-2" />
          <strong>Successfully authenticated</strong>
        </div>
        <div className="profile-info">
          <span>Profile: <strong>{state.selectedProfile || currentProfile}</strong></span>
        </div>
        {attributeNameValuePairs.length > 0 ? (
          <div className="attributes-container">
            <Table className="attributes-table">
              <thead>
                <tr>
                  <th>RADIUS Attribute</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {attributeNameValuePairs.map((attr, index) => (
                  <tr key={index}>
                    <td>{attr.name}</td>
                    <td>{attr.value}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-muted text-center">No attributes in the RADIUS response</div>
        )}
        <button className="btn-primary" onClick={handleLogout}>
          <Icon icon={FaSignOutAlt} className="me-2" />Sign Out
        </button>
      </div>
    );
  };

  // Modify the renderReject function
  const renderReject = () => {
    const { replyMessage, ipAddress } = state;

    return (
      <div className="login-card">
        <div className="logo-container">
          <img src={logo} alt="MidEye Logo" className="logo" />
        </div>
        <div className="status-message status-error">
          <Icon icon={FaTimesCircle} className="me-2" />
          <strong>Failed login</strong>
        </div>
        <p className="error-message">{replyMessage}</p>
        <button 
          className="btn-primary" 
          onClick={() => setState({...initialValues, ipAddress: ipAddress || '', selectedProfile: currentProfile})}
        >
          <Icon icon={FaSignInAlt} className="me-2" />Try Again
        </button>
      </div>
    );
  };

  const renderChallengePhase = () => {
    const { replyMessage } = state;

    return (
      <div className="login-card">
        <div className="logo-container">
          <img src={logo} alt="MidEye Logo" className="logo" />
        </div>
        <div className="profile-info">
          <span>Profile: <strong>{state.selectedProfile || currentProfile}</strong></span>
        </div>
        <Formik
          enableReinitialize
          initialValues={state}
          validationSchema={challengeValidationSchema}
          onSubmit={(values) => handleValidSubmit(values)}
        >
          {(props: FormikProps<any>) => (
            <Form id="radius-client-challenge-form">
              <div className="form-group">
                <label className="form-label">
                  <Icon icon={FaKey} className="me-2" />{replyMessage}
                </label>
                <Field 
                  id="otp-input"
                  name="otp" 
                  type="text" 
                  className="form-control"
                  data-testid="otp-field"
                  autoFocus
                  tabIndex={1}
                />
                <ErrorMessage
                  name="otp"
                  component="div"
                  className="field-error"
                />
              </div>
              <button type="submit" className="btn-primary" tabIndex={2}>
                <Icon icon={FaSignInAlt} className="me-2" />Submit
              </button>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  const renderBasedOnPhase = () => {
    const { authenticationPhase } = state;

    switch (authenticationPhase) {
      case "first":
        return renderFirstPhase();
      case "Accept":
        return renderAccept();
      case "Challenge":
        return renderChallengePhase();
      case "Reject":
        return renderReject();
      default:
        return authenticationPhase;
    }
  };

  return (
    <div className="login-container">
      {renderBasedOnPhase()}
    </div>
  );
};

export default App;
